/* eslint-disable id-denylist */
import React, { ReactElement, useMemo, useState } from 'react';
import { ZCDCard, ZCDDataVisualizationColor, ZCDExpandButton, ZCDSelect } from '@zencity/common-ui';
import { useTranslation } from 'react-i18next';
import { ColorByNumericId, ReportFilterProps } from 'screens/WidgetGenerator/reportGeneratorComponents/types';
import { FieldTitle } from 'screens/WidgetGenerator/components/FieldTitle/FieldTitle';
import { SelectOption } from '@zencity/common-ui/lib/ZCD/ZCDFilter/types';
import {
  concernsColorsOrder,
  extractSortedTopConcerns,
} from 'screens/WidgetGenerator/utils/answerClassificationBreakdown';
import { useAnswerClassificationSubTopicBreakdown } from 'screens/WidgetGenerator/reportGeneratorComponents/hooks/useAnswerClassificationSubTopicBreakdown';
import { logger } from 'utils/logger';

interface ColorOption {
  value?: string;
  label: string;
  color: string;
}

interface RelevantTopic {
  numericId: number;
  label: string;
}

const mapRelevantTopics = (relevantTopics: RelevantTopic[], value: ColorByNumericId | undefined) =>
  relevantTopics.map((topic) => {
    const options = concernsColorsOrder.map<ColorOption>((color) => ({
      value: color,
      label: topic.label,
      color: ZCDDataVisualizationColor[color],
    }));

    const selectedColor = value?.[topic.numericId];
    return {
      ...topic,
      options,
      selectedValue: options.find((option) => option.value === selectedColor),
    };
  });

export const OverrideColorsMappingFilter = (props: ReportFilterProps<ColorByNumericId>): ReactElement => {
  const { value: filterValue, onChange, filterValues, clientData } = props;
  const { t: translate } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  // Fetching the relevant topics to be displayed in the component.
  const { fetchedData, error } = useAnswerClassificationSubTopicBreakdown({
    filters: filterValues,
    clientData,
  });

  if (error) {
    logger.error(error);
  }

  const relevantTopics = useMemo<RelevantTopic[]>(() => {
    if (!fetchedData) {
      return [];
    }

    const topConcerns = extractSortedTopConcerns(fetchedData);
    return topConcerns.map((topic) => ({
      numericId: topic.id,
      label: topic.label,
    }));
  }, [fetchedData]);

  const handleColorSelection = (topicNumericId: number, selectedValue: SelectOption) => {
    const currentFilterValue = filterValue ?? {};
    const newFilterValue = {
      ...currentFilterValue,
    };
    if (selectedValue) {
      newFilterValue[topicNumericId] = selectedValue.value as keyof typeof ZCDDataVisualizationColor;
    } else {
      // Because there is no selected value for the current topic, remove it
      // from the structure before changing the filter's value.
      delete newFilterValue[topicNumericId];
    }

    onChange(newFilterValue);
  };

  const topics = useMemo(() => mapRelevantTopics(relevantTopics, filterValue), [relevantTopics, filterValue]);

  return (
    <ZCDCard>
      <ZCDExpandButton
        showLessLabel={translate('widgetGenerator.overrideColorsMapping')}
        showMoreLabel={translate('widgetGenerator.overrideColorsMapping')}
        isExpanded={isExpanded}
        onClick={() => setIsExpanded(!isExpanded)}
      />
      {isExpanded && (
        <>
          {!topics.length && <FieldTitle title={translate('widgetGenerator.noTopics')} />}
          {topics.map((topic) => (
            <ZCDSelect
              isClearable
              key={topic.numericId}
              placeholder={topic.label}
              options={topic.options}
              value={topic.selectedValue}
              onChange={(selectedValue: unknown) =>
                handleColorSelection(topic.numericId, selectedValue as SelectOption)
              }
              customStyles={{
                option: (styles, { data }) => ({
                  ...styles,
                  backgroundColor: data.color,
                  color: 'black',
                  cursor: 'pointer',
                }),
                singleValue: (styles, { data }) => ({ ...styles, backgroundColor: data.color }),
              }}
            />
          ))}
        </>
      )}
    </ZCDCard>
  );
};
